import { type ReactElement, useMemo, useState } from 'react';
import {
  Flex,
  IconChevronLeft,
  SafeAreaTopSpacing,
  Typography,
  HEADER_HEIGHT,
  HeaderIcon,
  useScreenSize,
} from '@banhala/ui-kit';
import { isNotNil } from 'shared/src/utils';
import { DropdownNavigatorTrigger } from './DropdownNavigator';
import type { ScreensMetaResponse } from 'shared/src/api';

type ScreensHeaderProps = {
  title?: string;
  dropdownNavigator: NonNullable<
    ScreensMetaResponse['screen']['dropdown_navigator']
  > | null;
  isDropdownOpen: boolean;
  onToggleDropdown: () => void;
  showBackButton: boolean;
  onPressBack?: () => void;
  rightIcons?: ReactElement[];
};

export const ScreensHeader = ({
  title,
  dropdownNavigator,
  isDropdownOpen,
  onToggleDropdown,
  showBackButton,
  onPressBack,
  rightIcons,
}: ScreensHeaderProps) => {
  const [leftIconWidth, setLeftIconWidth] = useState(40);
  const [rightIconWidth, setRightIconWidth] = useState(0);
  const { width: screenWidth } = useScreenSize();

  const headerIconWidth = useMemo(
    () => Math.max(leftIconWidth, rightIconWidth),
    [leftIconWidth, rightIconWidth],
  );

  return (
    <Flex backgroundColor="bg_always_white">
      <SafeAreaTopSpacing />
      <Flex.Row justifyContent="center">
        <Flex.Row
          width={screenWidth}
          height={HEADER_HEIGHT}
          justifyContent="space-between"
        >
          <Flex.Center
            position="absolute"
            left={0}
            right={0}
            top={0}
            bottom={0}
            horizontal
            padding={{ left: 2, right: 8 }}
          >
            <Flex width={headerIconWidth} />
            <Flex flex={1} alignItems="center">
              {dropdownNavigator ? (
                <DropdownNavigatorTrigger
                  selectedTitle={dropdownNavigator.selected_title}
                  isOpen={isDropdownOpen}
                  onToggle={onToggleDropdown}
                />
              ) : (
                isNotNil(title) && (
                  <Typography
                    type="h5"
                    numberOfLines={1}
                    color="content_primary"
                  >
                    {title}
                  </Typography>
                )
              )}
            </Flex>
            <Flex width={headerIconWidth} />
          </Flex.Center>

          <Flex.Row
            alignItems="center"
            justifyContent="center"
            padding={{ horizontal: 2 }}
            onLayout={({ nativeEvent }) =>
              setLeftIconWidth(nativeEvent.layout.width)
            }
          >
            {showBackButton && (
              <HeaderIcon onPress={onPressBack}>
                <IconChevronLeft fill="content_primary" />
              </HeaderIcon>
            )}
          </Flex.Row>

          <Flex.Row
            padding={{ right: 8 }}
            alignItems="center"
            onLayout={({ nativeEvent }) =>
              setRightIconWidth(nativeEvent.layout.width)
            }
          >
            {rightIcons}
          </Flex.Row>
        </Flex.Row>
      </Flex.Row>
    </Flex>
  );
};
